import React from 'react';
import { FeatureTourStep } from '../../components/featureTour/FeatureTour';

export const QUICK_VIEW_TOUR_ID = 'quick-view';
// Bump to show the tour again to everyone (for example after widgets are added that people should hear about).
export const QUICK_VIEW_TOUR_VERSION = 1;

interface QuickViewTourOptions {
    isQuickViewOpen: () => boolean;
    openQuickView: () => void;
    closeQuickView: () => void;
}

// The widget steps point at a widget by its data-tour label. A widget exists on screen only when the user has the access
// for it (QuickViewDrawer mounts each one behind its own UserFeatureEnum flag), so a step for a widget they do not have is
// skipped by the tour itself - nobody is told about something they cannot see.
const widgetTarget = (widgetKey: string) => `[data-tour="quick-view-widget-${widgetKey}"]`;

// The steps, plus a way to put the page back as the tour found it: the tour opens Quick View for its panel steps and closes
// it again afterwards if it was the one that opened it (a panel the user already had open - or pinned - is left alone).
export const createQuickViewTour = ({ isQuickViewOpen, openQuickView, closeQuickView }: QuickViewTourOptions) => {
    let didTourOpenQuickView = false;

    const showPanel = () => {
        if (!isQuickViewOpen()) {
            didTourOpenQuickView = true;
            openQuickView();
        }
    };

    const restorePage = () => {
        if (didTourOpenQuickView && isQuickViewOpen()) {
            closeQuickView();
        }
        didTourOpenQuickView = false;
    };

    const steps: FeatureTourStep[] = [
        {
            badge: 'New',
            title: 'Quick View',
            body: 'Your enquiries, tasks and numbers in one panel you can open from any page, without leaving what you are doing.',
        },
        {
            target: '[data-tour="quick-view-button"]',
            badge: 'Step 1 · Open',
            title: 'Open it from anywhere',
            body: <>Press this button in the header, on any page. Press it again, or the <b>✕</b> in the panel, to close it.</>,
            onEnter: restorePage,
        },
        {
            target: '[data-tour="quick-view-panel"]',
            placement: 'left',
            badge: 'Step 2 · Your day',
            title: 'Your day at a glance',
            body: <>One card for each part of your work you have access to. <b>Drag a card</b> by its top-right corner to put them in your own order, and press <b>↻</b> to refresh them all.</>,
            tip: 'Your order is remembered.',
            onEnter: showPanel,
        },
        {
            target: '[data-tour="quick-view-pin"]',
            placement: 'left',
            badge: 'Step 3 · Dock',
            title: 'Dock it beside your work',
            body: <><b>Pin</b> the panel and it stays open as you move between pages. The page slides over to make room, so nothing is hidden behind it. Unpin it, or press <b>✕</b>, when you are done.</>,
            tip: 'Docked or not is remembered the next time you sign in.',
            onEnter: showPanel,
        },
        {
            target: widgetTarget('yourEnquiries'),
            placement: 'left',
            badge: 'Enquiries',
            title: 'Enquiries that need you',
            body: <>How many of your enquiries are <b>not contacted yet</b>, have a <b>follow-up overdue</b> or are <b>waiting for a matter</b>. <b>Click a number</b> and the Enquiries list opens already filtered to just those.</>,
            tip: 'The "3+ days" pill shows how many have been waiting the longest.',
            onEnter: showPanel,
        },
        {
            target: widgetTarget('enquiryFollowUpsDue'),
            placement: 'left',
            badge: 'Enquiries',
            title: 'Follow-ups due',
            body: <>Your enquiries with a follow-up due today or earlier. <b>Click a row</b> to open that enquiry right here, beside the panel - no page change. <b>View all</b> opens the full list.</>,
            onEnter: showPanel,
        },
        {
            target: widgetTarget('yourTasks'),
            placement: 'left',
            badge: 'Tasks',
            title: 'Your tasks',
            body: <>How many of your tasks are queued, unassigned, not started or in progress. <b>Click one</b> to open your Tasks list.</>,
            onEnter: showPanel,
        },
        {
            target: widgetTarget('tasksDue'),
            placement: 'left',
            badge: 'Tasks',
            title: 'Tasks due',
            body: <><b>Click a task</b> to open and edit it in place. A task that belongs to an enquiry opens that enquiry. <b>View all</b> opens the full Tasks list.</>,
            onEnter: showPanel,
        },
        {
            target: widgetTarget('contactFollowUpsDue'),
            placement: 'left',
            badge: 'Contacts',
            title: 'Contact follow-ups',
            body: <>Follow-up tasks for your clients and contacts. <b>Click a row</b> to open it here, or <b>View all</b> for the full list.</>,
            onEnter: showPanel,
        },
        {
            target: widgetTarget('kpiSummary'),
            placement: 'left',
            badge: 'Your numbers',
            title: 'Your numbers, and what is behind them',
            body: <>Switch the period at the top. <b>Click New Matters</b>, or the <b>debt and unbilled</b> rows at the bottom, to drill into the matters behind the number. The arrow in the header opens the full Fee Earner Dashboard.</>,
            tip: 'The red, amber and green dots show how each number is tracking.',
            onEnter: showPanel,
        },
        {
            badge: 'Done',
            title: 'You are all set',
            body: <>You can replay this any time with the <b>?</b> in Quick View's header.</>,
            onEnter: showPanel,
        },
    ];

    return { steps, restorePage };
};
